import React, {useEffect, useMemo, useState} from 'react';
import {Modal, Input, Select, Switch, message, Button, Spin, Tag} from 'antd';
import {useQueryClient} from '@tanstack/react-query';
import {FilePreview} from '../FilePreview';
import {uploadService} from '../../services/upload';
import {TagSelect} from '../TagSelect';
import './index.scss';
import {fileService} from '../../services/file';
import {getObsKey} from '../../utils/filePreview';

interface FileMetaModalProps {
  open: boolean;
  fileInfo: any;
  title?: string;
  readonly?: boolean;
  onCancel: () => void;
  onSave: (data: any) => Promise<void>;
}

export function FileMetaModal({
                                open,
                                fileInfo,
                                title,
                                readonly = false,
                                onCancel,
                                onSave,
                              }: FileMetaModalProps) {
  const queryClient = useQueryClient();
  const [aiTags, setAiTags] = useState<string[]>([]);
  const [customsTags, setCustomsTags] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [editable, setEditable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [reparseLoading, setReparseLoading] = useState(false);

  const fileCompleteRes = useMemo(() => fileInfo?.completeRes || fileInfo || {}, [fileInfo]);
  
  // 判断是否处理中
  const isProcessing = fileCompleteRes.status === 2;
  // 判断是否为不支持的文件类型
  const isUnsupportedType = fileCompleteRes.file_type === 'Unknown' || 
                           fileCompleteRes.description === 'AI还不会读取这类文件...';

  useEffect(() => {
    const obskey = getObsKey(fileCompleteRes);

    if (!open || !obskey) {
      setPreviewUrl('');
      return;
    }

    let cancelled = false;

    uploadService.getTempDownloadUrl({obskey})
      .then((res: any) => {
        if (cancelled) return;

        setPreviewUrl(typeof res === 'string' ? res : res?.data);
      });

    return () => {
      cancelled = true;
    };
  }, [open, fileCompleteRes]);

  useEffect(() => {
    if (!fileInfo) return;

    setAiTags(fileCompleteRes.tags || []);
    setCustomsTags(fileCompleteRes.custom_tags || []);
    setDescription(fileCompleteRes.description || '');
    setEditable(fileCompleteRes.visibility === 'edit');
  }, [fileCompleteRes, fileInfo]);

  async function handleSave() {
    const fileId = fileCompleteRes.file_id || fileCompleteRes.id;

    if (!fileId) {
      void message.warning('缺少文件ID');
      return;
    }

    setLoading(true);

    try {
      await onSave({
        id: fileId,
        description,
        visibility: editable ? 'edit' : 'read',
        tags: aiTags,
        custom_tags: customsTags || [],
        type: fileCompleteRes.type,
      });

      void message.success('保存成功');
      onCancel();
    } catch (e) {
      void message.error('保存失败');
    } finally {
      setLoading(false);
    }
  }

  async function handleReparse() {
    const fileId = fileCompleteRes.file_id || fileCompleteRes.id;

    if (!fileId) {
      void message.warning('缺少文件ID');
      return;
    }

    setReparseLoading(true);

    try {
      // 异步重新解析,直接关闭弹窗
      await fileService.reparseFile({
        file_id: fileId,
      });

      void message.success('文件已提交重新解析,请稍后查看');

      // 刷新文件列表，让页面显示"处理中"状态，从而触发定时轮询
      await queryClient.invalidateQueries({
        queryKey: ['file-list'],
      });

      onCancel();
    } catch (e) {
      message.error('提交重新解析失败');
    } finally {
      setReparseLoading(false);
    }
  }

  return (
    <Modal
      className={`drive-file-meta-modal ${readonly ? 'drive-file-meta-modal--readonly' : ''}`}
      title={title || '编辑文件信息'}
      visible={open}
      onCancel={onCancel}
      closable={reparseLoading ? false : undefined}
      maskClosable={readonly}
      footer={readonly ? null : [
        <Button key="cancel" disabled={reparseLoading} onClick={onCancel}>
          取消
        </Button>,
        <Button 
          key="reparse" 
          loading={reparseLoading} 
          disabled={loading || isUnsupportedType} 
          onClick={handleReparse}
        >
          重新解析
        </Button>,
        <Button key="save" type="primary" loading={loading} disabled={reparseLoading} onClick={handleSave}>
          保存
        </Button>
      ]}
      width={1200}
      destroyOnClose
    >
      <Spin spinning={reparseLoading} tip="正在重新解析...">
        <div className="drive-file-meta">
          <div className="drive-file-meta__preview">
            <div className="drive-file-meta__preview-inner">
              <FilePreview file={fileCompleteRes} url={previewUrl}/>
            </div>
          </div>

          <div className="drive-file-meta__form">
            <div className="drive-file-meta__section">
              <div className="drive-file-meta__label">自定义标签</div>
              <TagSelect
                value={customsTags}
                onChange={setCustomsTags}
                disabled={readonly}
              />
            </div>

            <div className="drive-file-meta__section">
              <div className="drive-file-meta__label">AI解析标签</div>
              <div className="drive-file-meta__ai-tags">
                {aiTags.map((tag, index) => (
                  <Tag
                    key={index}
                    closable={!readonly && !isProcessing && !isUnsupportedType}
                    onClose={() => {
                      const nextTags = aiTags.filter((_, i) => i !== index);
                      setAiTags(nextTags);
                    }}
                    className="drive-file-meta__ai-tag"
                  >
                    {tag}
                  </Tag>
                ))}
                {!aiTags.length && (
                  <span className="drive-file-meta__empty">
                    {isUnsupportedType ? 'AI还不会读取这类文件...' : '暂无解析标签'}
                  </span>
                )}
              </div>
              <div className="drive-file-meta__hint">
                可删除不准确的 AI 标签
              </div>
            </div>

            <div className="drive-file-meta__section">
              <div className="drive-file-meta__label">AI文件描述</div>
              <Input.TextArea
                disabled={readonly || isProcessing || isUnsupportedType}
                rows={5}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="请输入文件描述"
              />
            </div>

            {/*<div className="drive-file-meta__permission">*/}
            {/*  <div>*/}
            {/*    <div className="drive-file-meta__permission-main">*/}
            {/*      文件权限*/}
            {/*    </div>*/}
            {/*    <div className="drive-file-meta__permission-sub">*/}
            {/*      {editable ? '允许读写操作' : '仅允许查看'}*/}
            {/*    </div>*/}
            {/*  </div>*/}

            {/*  <Switch*/}
            {/*    disabled={readonly}*/}
            {/*    checked={editable}*/}
            {/*    onChange={setEditable}*/}
            {/*    checkedChildren="读写"*/}
            {/*    unCheckedChildren="只读"*/}
            {/*  />*/}
            {/*</div>*/}
          </div>
        </div>
      </Spin>
    </Modal>
  );
}